/* eslint-disable react-hooks/exhaustive-deps */
import { Select, Text } from "@mantine/core";
import { useStyles } from "./IndividualDetails.styles";
import { FC, useState, useEffect, useCallback, useRef } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { Button, color } from "@funded-here-interface/common";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Data, UserData } from "../../types/org.type";
import { kycStatuses } from "@funded-here-interface/common/src/constant/constant";
import BankDetails from "../KycTabs/BankDetails/BankDetails";
import Documents from "../KycTabs/Documents/Documents";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import Individual from "../KycTabs/Individual/Individual";
import useGetOrgMutation from "../../hooks/useGetOrg";
import useUpdateKyc from "../../hooks/useUpdateKyc";
import Agreements from "../KycTabs/Agreements/Agreements";
import useOrganizationRisk from "../../hooks/useOrganizationRisk";
import { Risk } from "../../constant/enum";
import { riskValues } from "../../constant/risk";
import InfoDocWallex from "../KycTabs/InfoDocWallex/InfoDocWallex";
import { RegtankOnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import Note from "../KycTabs/Note/Note";
import RemarkHistory from "../KycTabs/RemarksHistory/RemarkHistory";
import EmailContentHistory from "../KycTabs/EmailContent/EmailContentHistory";
import EmailContent from "../KycTabs/EmailContent/EmailContent";
import Regtank from "../KycTabs/Regtank/Regtank";

interface IProps {
  close: () => void;
  id: number;
  userId: number | null;
  refetchList: () => void;
}

/**
 * Tab configuration interface for dynamic tab rendering
 */
interface TabConfig {
  id: string;
  label: string;
  /** Optional visibility condition function */
  isVisible?: (params: {
    signedDocuments: any[];
    selectedValue: string;
    kycStatus?: string;
  }) => boolean;
}

/**
 * Tab configuration array
 * Centralizes tab definitions to eliminate duplicate JSX
 */
const TAB_CONFIGS: TabConfig[] = [
  { id: "individual", label: "Individual" },
  { id: "bank-details", label: "Bank Details" },
  { id: "documents", label: "Documents" },
  {
    id: "agreements",
    label: "Agreements",
    isVisible: ({ signedDocuments }) => signedDocuments.length > 0,
  },
  { id: "regtank", label: "Regtank Verification" },
  { id: "remarks", label: "Remarks" },
  { id: "remarks-history", label: "Remarks History" },
  {
    id: "wallex",
    label: "Info & Doc to Wallex",
    isVisible: ({ selectedValue, kycStatus }) =>
      selectedValue === KYCStatus.APPROVED && kycStatus === "approved",
  },
];

const IndividualDetails: FC<IProps> = ({ close, id, refetchList, userId }) => {
  const [tab, setTab] = useState("individual");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setdata] = useState<Data>();
  const [userData, setUserData] = useState<UserData>();
  const [allUsersData, setAllUsersData] = useState<UserData[]>();
  const { token, regtankOnboardingFeatureFlag } = useSelector(
    (state: RootState) => state.auth
  );
  const getOrgMutation = useGetOrgMutation();
  const udpdateKycMutation = useUpdateKyc();
  const useOrganizationRiskMutation = useOrganizationRisk();
  const [selectedRisk, setSelectedRisk] = useState<Risk | undefined>();

  const [orgNote, setOrgNote] = useState<string>("");
  const { classes } = useStyles();
  const [disableSaveBtn, setDisableSaveBtn] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);
  const isSavingRef = useRef(false);

  /**
   * Shows a toast error and scrolls to the heading
   */
  const showErrorAndScrollToTop = useCallback((message: string) => {
    toast.error(message);
    headingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const [selectedValue, setSelectedValue] = useState<string>(
    data?.kycStatus || KYCStatus.PENDING_APPROVAL
  );

  useEffect(() => {
    if (data?.kycStatus) {
      setSelectedValue(data.kycStatus);
    }
  }, [data?.kycStatus]);

  const handleSelectChange = (value: string) => {
    setSelectedValue(value);
  };

  useEffect(() => {
    getOrgMutation.mutate(
      {
        token,
        id: `${id}`,
      },
      {
        onSuccess: (data) => {
          setdata(data);
          setAllUsersData(data.user);
          setUserData(data.user[0]);
          setSelectedRisk(data?.risk);
        },
        onError: (e) => {
          showErrorAndScrollToTop((e as Error).message);
        },
      }
    );
  }, [id]);

  const handleRiskSelect = (risk: Risk) => {
    setSelectedRisk(risk);

    useOrganizationRiskMutation.mutate(
      { token, risk, orgId: data?.id! },
      {
        onError: (e) => {
          showErrorAndScrollToTop((e as Error).message);
        },
      }
    );
  };

  const handleNext = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    if (selectedValue === KYCStatus.APPROVED) {
      const userRegtankOnboarding = userData?.regtankOnboarding;
      if (
        regtankOnboardingFeatureFlag === true &&
        userRegtankOnboarding === true
      ) {
        const userRegtankRequest = userData?.regtankRequest?.sort(
          (a, b) =>
            new Date(b.expiredDate).getTime() -
            new Date(a.expiredDate).getTime()
        );

        if (
          userRegtankRequest &&
          userRegtankRequest.length > 0 &&
          userRegtankRequest?.[0].onboardingStatus !==
            RegtankOnboardingStatusEnum.APPROVED
        ) {
          showErrorAndScrollToTop(
            "The user has not completed Regtank onboarding identity verification"
          );
          isSavingRef.current = false;
          return;
        }
      }

      if (!userData?.declarationAISubmitted) {
        showErrorAndScrollToTop("Investor Agreement has not been signed");
        isSavingRef.current = false;
        return;
      }
    }

    if (
      selectedValue === KYCStatus.PENDING_APPROVAL ||
      selectedValue === data?.kycStatus
    ) {
      isSavingRef.current = false;
      close();

      return;
    }

    const orgRoleId = userData?.orgRole?.[0]?.id;

    setIsLoading(true);
    udpdateKycMutation.mutate(
      {
        orgRoleId: orgRoleId as number,
        token,
        kycStatus: selectedValue as KYCStatus,
      },
      {
        onSuccess: () => {
          isSavingRef.current = false;
          setIsLoading(false);
          refetchList();
          close();
        },
        onError: (e) => {
          isSavingRef.current = false;
          setIsLoading(false);
          showErrorAndScrollToTop((e as Error).message);
        },
      }
    );
  };

  const signedDocuments = data?.documentsUpload?.signedDocuments
    ? data?.documentsUpload?.signedDocuments.filter(
        (doc) =>
          !(
            doc.docName === "membership_agreement" &&
            !userData?.agreedToMembershipAgreement
          )
      )
    : [];

  /**
   * Renders content for the active tab
   * Centralizes all tab content rendering logic
   */
  const renderTabContent = useCallback(() => {
    switch (tab) {
      case "individual":
        return <Individual data={data} userData={userData} />;
      case "bank-details":
        return <BankDetails userData={userData} />;
      case "documents":
        return <Documents data={data} userData={userData} />;
      case "agreements":
        return signedDocuments.length > 0 ? (
          <Agreements signedDocuments={signedDocuments} />
        ) : null;
      case "regtank":
        return <Regtank orgData={data} userData={allUsersData} />;
      case "remarks":
        return (
          <div>
            <Text className={classes.subHeadingText}>Internal Remarks</Text>
            <Note userId={userId} initialNote={orgNote} type="regular" />
            <div style={{ marginTop: "20px" }}>
              <Text className={classes.subHeadingText}>
                Remarks for Onboarding Details Resubmission
              </Text>
              <EmailContent data={data} userData={userData} companyName={""} />
            </div>
          </div>
        );
      case "remarks-history":
        return (
          <div>
            <Text className={classes.subHeadingText}>Internal Remarks</Text>
            <RemarkHistory userId={userId} type="regular" />
            {data?.spv && (
              <div style={{ marginTop: "20px" }}>
                <Text className={classes.subHeadingText}>
                  Internal Remarks (SPV)
                </Text>
                <RemarkHistory userId={userId} type="spv" />
              </div>
            )}
            <div style={{ marginTop: "20px" }}>
              <Text className={classes.subHeadingText}>
                Remarks for Onboarding Details Resubmission
              </Text>
              <EmailContentHistory userId={userId} />
            </div>
          </div>
        );
      case "wallex":
        return (
          <InfoDocWallex data={data} userData={userData} user={allUsersData} />
        );
      default:
        return null;
    }
  }, [
    tab,
    data,
    userData,
    allUsersData,
    signedDocuments,
    userId,
    orgNote,
    classes.subHeadingText,
  ]);

  return (
    <div className={classes.OuterContainer} ref={headingRef}>
      <div className={classes.innerContainer}>
        <ToastContainer />

        <AiOutlineClose
          onClick={() => {
            close();
            setSelectedValue("");
          }}
          className={classes.closeIcon}
        />

        <Text className={classes.heading}>Individual KYC Details</Text>
        <div className={classes.SubHeadingOutterContainer}>
          <div className={classes.subHeadingContainer}>
            <Text className={classes.subHeadingText}>KYC Status</Text>
            <Select
              className={classes.KycSelect}
              data={kycStatuses.map((status) => ({
                value: status,
                label: status,
                disabled: status === KYCStatus.PENDING_RESUBMISSION,
              }))}
              value={selectedValue}
              onChange={handleSelectChange}
              size="md"
              variant="filled"
              radius="md"
              disabled={data?.kycStatus === KYCStatus.PENDING_RESUBMISSION}
              styles={{
                dropdown: {
                  minWidth: "200px", // Ensure dropdown is wide enough for longer text
                },
                item: {
                  whiteSpace: "nowrap", // Prevent text wrapping in dropdown items
                },
              }}
            />
            {data?.kycStatus === KYCStatus.PENDING_RESUBMISSION && (
              <Text size="sm" color="dimmed" mt={4}>
                Status locked. Awaiting user resubmission.
              </Text>
            )}
          </div>
          <div className={classes.subHeadingContainer}>
            <Text className={classes.subHeadingText}>Customer Risk</Text>
            <Select
              className={classes.KycSelect}
              data={riskValues}
              value={selectedRisk}
              placeholder="Select Risk"
              onChange={handleRiskSelect}
              size="md"
            />
          </div>
        </div>
        <div>
          <ul className={classes.listContainer}>
            {TAB_CONFIGS.filter((tabConfig) => {
              // Show tab if no visibility condition exists, or if condition is met
              if (!tabConfig.isVisible) return true;
              return tabConfig.isVisible({
                signedDocuments,
                selectedValue,
                kycStatus: data?.kycStatus,
              });
            }).map((tabConfig) => (
              <li
                key={tabConfig.id}
                onClick={() => setTab(tabConfig.id)}
                className={`${classes.listText} ${
                  tab === tabConfig.id && classes.stepActive
                }`}
              >
                {tabConfig.label}
              </li>
            ))}
          </ul>
          <div className={classes.contentContainer}>{renderTabContent()}</div>
          {tab === "individual" && (
            <div className={classes.buttonWrapper}>
              <Button
                disabled={isLoading || disableSaveBtn}
                onClick={handleNext}
                backgroundColor={color.FHADMIN}
                textColor={color.WHITE}
                width="80%"
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndividualDetails;
